import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "components/DesignSystem/Button/Button";
import { PlusIcon } from "components/icons/PlusIcon";
import { EditPencil } from "components/icons/EditPencil";
import { DeleteIcon } from "components/icons/delete";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { AddEntityForm } from "v2/components/onboarding/steps/ConfirmEntityStep/AddEntityForm";
import { OnboardingEntityDraft } from "v2/components/onboarding/OnboardingDataContext";
import {
  EntityReviewItem,
  useAddGroupEntityMutation,
  useDeleteOnboardingEntityMutation,
  useGetGroupReviewDetailsQuery,
  useUpdateOnboardingEntityMutation,
} from "v2/store/onboardingEntityApi";
import { emptyApi } from "store/apis/emptyApi";
import { joinLegalName } from "v2/utils/joinLegalName";
import { toEntityInput } from "v2/utils/toEntityInput";
import { useGroupId } from "v2/utils/useGroupId";

type AddedEntity = { entityId: string; name: string };

const EMPTY_ENTITIES: AddedEntity[] = [];
const EMPTY_RELATION_OPTIONS: { value: string; label: string }[] = [];
const GENERIC_ERROR = "Something went wrong";
const TAX_ONBOARDING_SOURCE = "TAX_ONBOARDING";
const DETECTED_FILINGS_TAG = "TAX_DETECTED_FILINGS";

const toEntityDraft = (review: EntityReviewItem): OnboardingEntityDraft => ({
  localId: review.entityId,
  entityId: review.entityId,
  entityName: review.entityName,
  entitySuffix: review.entitySuffix,
  formationTypeId: review.formationTypeId,
  entityTypeLabel: review.entityTypeLabel,
  taxedAs: review.taxedAs,
  addressData: review.addressData,
  isPrimary: false,
  cStateId: review.homeStateId ?? undefined,
  dateOfIncorporation: review.dateOfIncorporation || undefined,
});

const toFallbackDraft = (entity: AddedEntity): OnboardingEntityDraft => ({
  localId: entity.entityId,
  entityId: entity.entityId,
  entityName: entity.name,
  entitySuffix: "",
  formationTypeId: "",
  entityTypeLabel: "",
  addressData: null,
  isPrimary: false,
});

const SubsidiaryEntityRow = ({
  name,
  onEdit,
  onRemove,
}: {
  name: string;
  onEdit: () => void;
  onRemove: () => void;
}) => (
  <div className="t-flex t-items-center t-justify-between t-rounded-lg t-border t-border-solid t-border-neutral-10 t-px-3 t-py-2">
    <span className="t-text-body-sm t-text-text-100">{name}</span>

    <div className="t-flex t-items-center t-gap-1">
      <Button
        type="button"
        customType="ghost_icon"
        size="small"
        onClick={onEdit}
      >
        <span className="t-text-text-30">
          <EditPencil />
        </span>
      </Button>

      <Button
        type="button"
        customType="ghost_icon"
        size="small"
        onClick={onRemove}
      >
        <span className="t-text-text-30">
          <DeleteIcon />
        </span>
      </Button>
    </div>
  </div>
);

export const SubsidiaryEntityList = ({
  parentEntityId,
  initialEntities = EMPTY_ENTITIES,
}: {
  parentEntityId?: string;
  initialEntities?: AddedEntity[];
}) => {
  const groupId = useGroupId();
  const dispatch = useDispatch();
  const { successToast, alertToast } = useToast();

  const { data: countries } = useGetCountriesDataQuery();

  const { data: reviewEntities } = useGetGroupReviewDetailsQuery(
    { groupId },
    { skip: !groupId }
  );

  const refetchDetectedFilings = (): void => {
    dispatch(emptyApi.util.invalidateTags([DETECTED_FILINGS_TAG]));
  };

  const addForm = useModal();

  const [addGroupEntity] = useAddGroupEntityMutation();
  const [updateOnboardingEntity] = useUpdateOnboardingEntityMutation();
  const [deleteOnboardingEntity] = useDeleteOnboardingEntityMutation();

  const [entities, setEntities] = useState<AddedEntity[]>(initialEntities);
  const [editingEntityId, setEditingEntityId] = useState<string | null>(null);

  const isSeeded = useRef(initialEntities.length > 0);

  useEffect(() => {
    if (isSeeded.current || initialEntities.length === 0) return;
    setEntities(initialEntities);
    isSeeded.current = true;
  }, [initialEntities]);

  const showError = (error: unknown): void => {
    const message =
      (error as { data?: { message?: string } })?.data?.message ??
      GENERIC_ERROR;
    alertToast({ message });
  };

  const handleSave = async (draft: OnboardingEntityDraft): Promise<void> => {
    try {
      const input = toEntityInput(draft, TAX_ONBOARDING_SOURCE);

      const added = await addGroupEntity({ groupId, input }).unwrap();

      if (parentEntityId) {
        await updateOnboardingEntity({
          entityId: added.entityId,
          input,
          relation: { parentId: parentEntityId, childId: added.entityId },
        }).unwrap();
      }

      const name =
        joinLegalName(draft.entityName, draft.entitySuffix) || "Entity";
      setEntities((prev) => [...prev, { entityId: added.entityId, name }]);
      addForm.close();
      refetchDetectedFilings();
      successToast({ message: "Company added" });
    } catch (error) {
      showError(error);
    }
  };

  const handleUpdate = async (draft: OnboardingEntityDraft): Promise<void> => {
    const entityId = draft.entityId;
    if (!entityId) return;

    try {
      await updateOnboardingEntity({
        entityId,
        input: toEntityInput(draft, TAX_ONBOARDING_SOURCE),
        relation: parentEntityId
          ? { parentId: parentEntityId, childId: entityId }
          : undefined,
      }).unwrap();

      const name =
        joinLegalName(draft.entityName, draft.entitySuffix) || "Entity";
      setEntities((prev) =>
        prev.map((entity) =>
          entity.entityId === entityId ? { ...entity, name } : entity
        )
      );
      setEditingEntityId(null);
      refetchDetectedFilings();
      successToast({ message: "Company updated" });
    } catch (error) {
      showError(error);
    }
  };

  const handleCancelEdit = (): void => setEditingEntityId(null);

  const handleRemove = async (entityId: string): Promise<void> => {
    try {
      await deleteOnboardingEntity({ entityId }).unwrap();
      setEntities((prev) =>
        prev.filter((entity) => entity.entityId !== entityId)
      );
      refetchDetectedFilings();
    } catch (error) {
      showError(error);
    }
  };

  const draftByEntityId = new Map(
    (reviewEntities ?? []).map((review) => [
      review.entityId,
      toEntityDraft(review),
    ])
  );

  const entityRows = entities.map((entity) => {
    const draft =
      draftByEntityId.get(entity.entityId) ?? toFallbackDraft(entity);

    if (editingEntityId === entity.entityId) {
      return (
        <AddEntityForm
          key={entity.entityId}
          initial={draft}
          mode="separate"
          relationOptions={EMPTY_RELATION_OPTIONS}
          countries={countries}
          showBusinessAddress={false}
          showStateField
          requireStateField
          showDateField
          singleColumn
          onSave={handleUpdate}
          onCancel={handleCancelEdit}
        />
      );
    }

    return (
      <SubsidiaryEntityRow
        key={entity.entityId}
        name={entity.name}
        onEdit={() => setEditingEntityId(entity.entityId)}
        onRemove={() => handleRemove(entity.entityId)}
      />
    );
  });

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {entities.length > 0 && (
        <div className="t-flex t-flex-col t-gap-2">{entityRows}</div>
      )}
      {addForm.isOpen ? (
        <AddEntityForm
          mode="separate"
          relationOptions={EMPTY_RELATION_OPTIONS}
          countries={countries}
          showBusinessAddress={false}
          showStateField
          requireStateField
          showDateField
          singleColumn
          onSave={handleSave}
          onCancel={addForm.close}
        />
      ) : (
        <Button
          type="button"
          customType="secondary"
          size="small"
          leftIcon={<PlusIcon size="12" />}
          onClick={addForm.open}
        >
          Add entity
        </Button>
      )}
    </div>
  );
};
